import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { LogOut, User, FileText, Scale, Heart, Briefcase, Building, Loader2, Save, Check, ChevronDown, ClipboardList, UserCircle, MapPin, Users } from 'lucide-react';
import { toast } from 'sonner';
import IntakeInfoStep from '../components/intake/IntakeInfoStep';
import PersonalInfoStep from '../components/intake/PersonalInfoStep';
import ResidenceStep from '../components/intake/ResidenceStep';
import MaritalStatusStep from '../components/intake/MaritalStatusStep';
import WillsStep from '../components/intake/WillsStep';
import POAStep from '../components/intake/POAStep';
import HealthcareStep from '../components/intake/HealthcareStep';
import AssetsStep from '../components/intake/AssetsStep';
import TrustsStep from '../components/intake/TrustsStep';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Phone number formatting function - formats to (555) 555-5555
export function formatPhoneNumber(value) {
    if (!value) return '';
    const digits = value.replace(/\D/g, '');
    const limited = digits.slice(0, 10);
    if (limited.length === 0) return '';
    if (limited.length <= 3) return '(' + limited;
    if (limited.length <= 6) return '(' + limited.slice(0, 3) + ') ' + limited.slice(3);
    return '(' + limited.slice(0, 3) + ') ' + limited.slice(3, 6) + '-' + limited.slice(6);
}

const sections = [
    { id: 'intake_info', title: 'A. Intake Information', icon: ClipboardList, component: IntakeInfoStep },
    { id: 'personal_info', title: 'B. Client Personal Information', icon: UserCircle, component: PersonalInfoStep },
    { id: 'residence', title: 'C. Residence & Jurisdiction', icon: MapPin, component: ResidenceStep },
    { id: 'marital_status', title: 'D. Marital Status', icon: Users, component: MaritalStatusStep },
    { id: 'wills', title: 'E. Wills', icon: FileText, component: WillsStep },
    { id: 'poa', title: 'F. Power of Attorney', icon: Scale, component: POAStep },
    { id: 'healthcare', title: 'G. Advance Medical Directive', icon: Heart, component: HealthcareStep },
    { id: 'assets', title: 'H. Assets', icon: Briefcase, component: AssetsStep },
    { id: 'trusts', title: 'I. Trusts', icon: Building, component: TrustsStep },
];

const initialFormData = {
    first_name: '', middle_name: '', last_name: '', date_of_birth: '', ssn_last_four: '',
    pronouns: '',
    email: '', phone: '', address: '', city: '', state: '', zip_code: '',
    drivers_license_state: '', drivers_license_number: '', drivers_license_expiration: '',
    locality_type: 'county', locality_name: '',
    marital_status: 'single',
    status: 'active_not_retained',
    attorney_assigned: '', intake_coordinator: '', referral_source: '', consultation_date: '', 
    fee_amount: '', services_requested: {}, cost_factors: {}, intake_notes: '',
    attorney_recommendation: '', recommendation_reason: '', cost_quoted: '',
    // Centralized people list - enter once, use everywhere
    people: [],
    spouse: { name: '', date_of_birth: '', ssn_last_four: '', email: '', phone: '', gender: '' },
    former_spouses: [],
    children: [], beneficiaries: [], assets: [],
    healthcare_directive: { has_living_will: false, dnr_order: false, organ_donor: false, healthcare_agent_name: '', healthcare_agent_phone: '', healthcare_agent_relationship: '', alternate_agent_name: '', alternate_agent_phone: '', special_instructions: '' },
    power_of_attorney: { financial_poa_name: '', financial_poa_phone: '', financial_poa_relationship: '', financial_poa_alternate: '', durable: true, springing: false },
    executor: { primary_name: '', primary_phone: '', primary_relationship: '', primary_gender: '', alternate_name: '', alternate_gender: '' },
    trust: { trust_type: '', trustee_name: '', trust_name: '', trustee_relationship: '', successor_trustee_name: '', successor_trustee_relationship: '', trust_purpose: '', distribution_instructions: '', special_instructions: '' },
    final_wishes: { disposition_method: 'cremation', disposition_instructions: '', distribution_type: 'per_stirpes', disinherited_persons: '', minor_children_wishes: '' },
    special_instructions: '', notes: ''
};

function ClientIntake() {
    const auth = useAuth();
    const navigate = useNavigate();
    const params = useParams();
    const clientId = params.id;
    const isEditMode = Boolean(clientId);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(isEditMode);
    const [form, setForm] = useState(initialFormData);
    const [savedClientId, setSavedClientId] = useState(clientId || null);
    const [expandedSections, setExpandedSections] = useState({
        intake_info: false,
        personal_info: false,
        residence: false,
        marital_status: false,
        wills: false,
        poa: false,
        healthcare: false,
        assets: false,
        trusts: false
    });
    
    const sectionRefs = useRef({});

    useEffect(() => {
        if (isEditMode) {
            loadClient();
        }
    }, [clientId]);

    async function loadClient() {
        try {
            const res = await axios.get(API_URL + '/api/clients/' + clientId);
            const clientData = res.data;
            setForm({
                ...initialFormData,
                ...clientData,
                spouse: { ...initialFormData.spouse, ...(clientData.spouse || {}) },
                healthcare_directive: { ...initialFormData.healthcare_directive, ...(clientData.healthcare_directive || {}) },
                power_of_attorney: { ...initialFormData.power_of_attorney, ...(clientData.power_of_attorney || {}) },
                executor: { ...initialFormData.executor, ...(clientData.executor || {}) },
                trust: { ...initialFormData.trust, ...(clientData.trust || {}) },
                final_wishes: { ...initialFormData.final_wishes, ...(clientData.final_wishes || {}) },
            });
        } catch (e) {
            toast.error('Failed to load client data');
            navigate('/clients');
        } finally {
            setLoading(false);
        }
    }

    const updateForm = (updates) => setForm(prev => ({ ...prev, ...updates }));

    // Validation
    const primaryBeneficiaries = form.beneficiaries.filter(b => !b.contingent);
    const contingentBeneficiaries = form.beneficiaries.filter(b => b.contingent);
    const totalPrimaryPercentage = primaryBeneficiaries.reduce((sum, b) => sum + (parseFloat(b.percentage) || 0), 0);
    const totalContingentPercentage = contingentBeneficiaries.reduce((sum, b) => sum + (parseFloat(b.percentage) || 0), 0);

    function validate() {
        if (!form.first_name || !form.last_name) {
            toast.error('Please enter first and last name');
            scrollToSection('intake_info');
            return false;
        }
        if (primaryBeneficiaries.length > 0 && totalPrimaryPercentage !== 100) {
            toast.error('Primary beneficiary percentages must equal exactly 100% (currently ' + totalPrimaryPercentage + '%)');
            scrollToSection('wills');
            return false;
        }
        if (contingentBeneficiaries.length > 0 && totalContingentPercentage !== 100) {
            toast.error('Contingent beneficiary percentages must equal exactly 100% (currently ' + totalContingentPercentage + '%)');
            scrollToSection('wills');
            return false;
        }
        return true;
    }

    function scrollToSection(sectionId) {
        const idx = sections.findIndex(s => s.id === sectionId);
        if (idx !== -1 && sectionRefs.current[sectionId]) {
            sectionRefs.current[sectionId].scrollIntoView({ behavior: 'smooth', block: 'start' });
            setExpandedSections(prev => ({ ...prev, [sectionId]: true }));
        }
    }

    function scrollToNextSection(currentSectionId) {
        const idx = sections.findIndex(s => s.id === currentSectionId);
        if (idx < sections.length - 1) {
            const nextSection = sections[idx + 1];
            scrollToSection(nextSection.id);
        }
    }

    // Add a person to the centralized people list (avoids duplicates)
    function addPersonToList(person) {
        if (!person?.name || person.name.trim().length < 2) return;
        
        const trimmedName = person.name.trim();
        const existingPeople = form.people || [];
        
        // Check if already exists (by name, case-insensitive)
        const alreadyExists = existingPeople.some(
            p => p.name.toLowerCase() === trimmedName.toLowerCase()
        );
        
        if (!alreadyExists) {
            updateForm({ 
                people: [...existingPeople, { 
                    name: trimmedName, 
                    relationship: person.relationship || '',
                    phone: person.phone || ''
                }]
            });
        }
    }

    async function saveAndContinue(currentSectionId) {
        // For intake_info section, we don't require name yet (can save without client info)
        if (currentSectionId !== 'intake_info' && (!form.first_name || !form.last_name)) {
            toast.error('Please enter first and last name first');
            scrollToSection('client_info');
            return;
        }
        
        setSaving(true);
        try {
            if (savedClientId) {
                await axios.put(API_URL + '/api/clients/' + savedClientId, form);
                toast.success('Progress saved!');
            } else {
                // For intake_info without name, just move to next section without saving
                if (!form.first_name || !form.last_name) {
                    scrollToNextSection(currentSectionId);
                    setSaving(false);
                    return;
                }
                const res = await axios.post(API_URL + '/api/clients', form);
                setSavedClientId(res.data.id);
                toast.success('Client created! Progress saved.');
                // Update URL without full navigation
                window.history.replaceState(null, '', '/clients/' + res.data.id + '/edit');
            }
            scrollToNextSection(currentSectionId);
        } catch (e) {
            toast.error('Failed to save');
        } finally {
            setSaving(false);
        }
    }

    async function saveAndDone() {
        if (!validate()) return;
        
        setSaving(true);
        try {
            if (savedClientId) {
                await axios.put(API_URL + '/api/clients/' + savedClientId, form);
                toast.success('Client saved!');
                navigate('/clients/' + savedClientId);
            } else {
                const res = await axios.post(API_URL + '/api/clients', form);
                toast.success('Client saved!');
                navigate('/clients/' + res.data.id);
            }
        } catch (e) {
            toast.error('Failed to save');
        } finally {
            setSaving(false);
        }
    }

    function toggleSection(sectionId) {
        setExpandedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center">
                <div className="flex items-center gap-3">
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                    <span>Loading client data...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#FDFCF8]">
            <header className="sticky top-0 z-50 border-b border-stone-200 bg-white/80 backdrop-blur-sm">
                <div className="flex items-center justify-between px-6 py-4 max-w-7xl mx-auto">
                    <Link to="/dashboard" className="flex items-center gap-3">
                        <img src="/lion-logo.png" alt="BestWillsAndTrusts" className="h-10 w-10 object-contain" />
                        <span className="font-serif text-xl font-semibold hidden sm:block text-amber-600">BestWillsAndTrusts</span>
                    </Link>
                    <nav className="hidden md:flex items-center gap-8">
                        <Link to="/dashboard" className="text-sm font-medium text-muted-foreground">Dashboard</Link>
                        <Link to="/clients" className="text-sm font-medium text-primary">Clients</Link>
                        <Link to="/documents" className="text-sm font-medium text-muted-foreground">Documents</Link>
                        <Link to="/templates" className="text-sm font-medium text-muted-foreground">Templates</Link>
                        {auth.user?.role === 'admin' && <Link to="/admin/users" className="text-sm font-medium text-muted-foreground">Users</Link>}
                    </nav>
                    <div className="flex items-center gap-4">
                        <span className="text-sm text-muted-foreground hidden sm:block">{auth.user?.name}</span>
                        <Button variant="ghost" size="sm" onClick={auth.logout} className="rounded-none">
                            <LogOut className="h-4 w-4 mr-2" />Sign Out
                        </Button>
                    </div>
                </div>
            </header>

            <main className="max-w-5xl mx-auto px-6 py-8">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="font-serif text-3xl mb-2">{isEditMode ? 'Edit Client' : 'New Client Intake'}</h1>
                    <p className="text-muted-foreground">
                        {isEditMode ? 'Update client information below' : 'Complete all sections below. Use "Save & Continue" to save progress.'}
                    </p>
                    {savedClientId && !isEditMode && (
                        <p className="text-sm text-green-600 mt-2 flex items-center gap-1">
                            <Check className="h-4 w-4" /> Client created - your progress is being saved
                        </p>
                    )}
                </div>

                {/* Quick Nav */}
                <div className="mb-6 p-4 bg-stone-100 border border-stone-200">
                    <p className="text-sm font-medium text-muted-foreground mb-3">JUMP TO SECTION</p>
                    <div className="flex flex-wrap gap-2">
                        {sections.map((section) => {
                            const Icon = section.icon;
                            return (
                                <Button
                                    key={section.id}
                                    variant="outline"
                                    size="sm"
                                    onClick={() => scrollToSection(section.id)}
                                    className="rounded-none text-xs"
                                >
                                    <Icon className="h-3 w-3 mr-1" />
                                    {section.title}
                                </Button>
                            );
                        })}
                    </div>
                </div>

                {/* All Sections */}
                <div className="space-y-6">
                    {sections.map((section, idx) => {
                        const Icon = section.icon;
                        const Component = section.component;
                        const isExpanded = expandedSections[section.id];
                        const isLast = idx === sections.length - 1;

                        return (
                            <Card
                                key={section.id}
                                ref={el => sectionRefs.current[section.id] = el}
                                className="rounded-none border-stone-200 scroll-mt-24"
                            >
                                <CardHeader
                                    className="cursor-pointer hover:bg-stone-50 transition-colors"
                                    onClick={() => toggleSection(section.id)}
                                >
                                    <div className="flex items-center justify-between">
                                        <CardTitle className="font-serif text-xl flex items-center gap-3">
                                            <div className="p-2 bg-primary/10">
                                                <Icon className="h-5 w-5 text-primary" />
                                            </div>
                                            {section.title}
                                        </CardTitle>
                                        <ChevronDown className={`h-5 w-5 text-muted-foreground transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                    </div>
                                </CardHeader>
                                {isExpanded && (
                                    <CardContent className="border-t border-stone-100 pt-6">
                                        <Component form={form} updateForm={updateForm} addPersonToList={addPersonToList} />
                                        
                                        {/* Save buttons for each section */}
                                        <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-stone-200">
                                            {!isLast && (
                                                <Button
                                                    variant="outline"
                                                    onClick={() => saveAndContinue(section.id)}
                                                    disabled={saving}
                                                    className="rounded-none"
                                                    data-testid={`save-continue-${section.id}`}
                                                >
                                                    {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                                                    Save & Continue
                                                </Button>
                                            )}
                                            <Button
                                                onClick={saveAndDone}
                                                disabled={saving}
                                                className="rounded-none bg-primary"
                                                data-testid={`save-done-${section.id}`}
                                            >
                                                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
                                                Save & Done
                                            </Button>
                                        </div>
                                    </CardContent>
                                )}
                            </Card>
                        );
                    })}
                </div>

                {/* Bottom Save Bar */}
                <div className="sticky bottom-0 mt-8 p-4 bg-white border border-stone-200 shadow-lg flex items-center justify-between">
                    <div className="text-sm text-muted-foreground">
                        {savedClientId ? (
                            <span className="text-green-600 flex items-center gap-1">
                                <Check className="h-4 w-4" /> Progress saved
                            </span>
                        ) : (
                            'Fill out required fields to save'
                        )}
                    </div>
                    <div className="flex gap-3">
                        <Button variant="outline" onClick={() => navigate('/clients')} className="rounded-none">
                            Cancel
                        </Button>
                        <Button onClick={saveAndDone} disabled={saving} className="rounded-none bg-primary">
                            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
                            Save & Done
                        </Button>
                    </div>
                </div>
            </main>
        </div>
    );
}

export default ClientIntake;
